import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_DARK, NAV } from "@/lib/site";

export const Header = ({ onQuote }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-black/5 py-3"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <a
          data-testid="logo-home"
          href="#top"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="flex items-center gap-3 group"
        >
          <img src={LOGO_DARK} alt="Fascinated by Floors" className="h-11 w-11 object-contain" />
          <span className="leading-none">
            <span className="block font-display text-lg tracking-tight text-royal-deep">Fascinated by Floors</span>
            <span className="block text-[10px] tracking-[0.24em] uppercase text-muted-foreground">Beyond Expectations</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <button
              key={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              onClick={() => go(n.href)}
              className="relative text-sm font-medium text-royal-deep/80 hover:text-royal transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 hover:after:w-full after:bg-royal after:transition-all after:duration-300"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-quote-btn"
            onClick={onQuote}
            className="hidden sm:inline-flex items-center gap-2 bg-royal text-white text-sm font-medium pl-5 pr-4 py-2.5 rounded-full hover:bg-royal-dark transition-colors duration-300 group"
          >
            Request a Quote
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden h-10 w-10 grid place-items-center rounded-full border border-black/10 text-royal-deep"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white border-t border-black/5"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <button
                  key={n.href}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  onClick={() => go(n.href)}
                  className="text-left font-display text-2xl text-royal-deep"
                >
                  {n.label}
                </button>
              ))}
              <button
                data-testid="mobile-quote-btn"
                onClick={() => { setOpen(false); onQuote(); }}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-royal text-white py-3 rounded-full"
              >
                Request a Quote <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
